import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  confirmStationDelete,
  fetchRegionCafesAsync,
  fetchRegionsAsync,
  fetchSelectedCafeAndStationsAsync,
  fetchStationItemBank,
  setSelectedStation,
} from "../../reducers/diningReducer";
import {
  cafeListSelector,
  cafeSelector,
  regionListSelector,
  stationListSelector,
  stationSelector,
} from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import { ModalType } from "../../common/commonConstants";
import { useRestoreFocusOnDialogClose } from "../DialogBox";
import withAppInsights from "../AppInsights/AppInsights";
import StationSelectorView from "./StationSelectorView";

// Fluent v9 dropdown option type
export type SelectOption = { key: string; text: string };

const regionOptionsSelector = createSelector(regionListSelector, (regions) =>
  regions.map((r) => ({ key: r.id, text: r.name ?? "" })),
);

const cafeOptionsSelector = createSelector(cafeListSelector, (cafes) =>
  cafes.map((c) => ({ key: c.id, text: c.name ?? "" })),
);

const stationOptionsSelector = createSelector(stationListSelector, (stations) =>
  stations.map((s) => ({ key: s.id, name: s.name })),
);

const StationSelector = () => {
  const dispatch = useAppDispatch();
  const captureTrigger = useRestoreFocusOnDialogClose(ModalType.CONFIRM_DELETE_STATION);

  const regionOptions = useSelector(regionOptionsSelector);
  const cafeOptions = useSelector(cafeOptionsSelector);
  const stationOptions = useSelector(stationOptionsSelector);

  const selectedCafe = useSelector(cafeSelector);
  const selectedStation = useSelector(stationSelector);

  // Load regions on init
  useEffect(() => {
    if (!regionOptions.length) dispatch(fetchRegionsAsync());
  }, []);

  const onRegionSelect = (option?: SelectOption) => {
    if (option) dispatch(fetchRegionCafesAsync(option.key));
  };

  const onCafeSelect = (option?: SelectOption) => {
    if (option) dispatch(fetchSelectedCafeAndStationsAsync(option.key));
  };

  const onStationClick = (station: { key: string; name: string }) => {
    if (selectedCafe?.id) {
      dispatch(setSelectedStation({ id: station.key, name: station.name }));
      dispatch(fetchStationItemBank(station.key, selectedCafe.id));
    }
  };

  const onStationDeleteClick = () => {
    captureTrigger();
    dispatch(confirmStationDelete());
  };

  return (
    <StationSelectorView
      regionOptions={regionOptions}
      cafeOptions={cafeOptions}
      stationOptions={stationOptions}
      onRegionSelect={onRegionSelect}
      onCafeSelect={onCafeSelect}
      onStationClick={onStationClick}
      selectedRegionId={selectedCafe?.regionId}
      selectedCafeId={selectedCafe?.id}
      selectedStation={selectedStation?.id}
      onStationDeleteClick={onStationDeleteClick}
    />
  );
};

export default withAppInsights(StationSelector);
